import type { Asset, AssetGroup, SafeLuminary, Scene } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { getAssetAppearanceOffset } from 'js/src/agent/video-state/serialization/assetTimingDescription';

import { resolveOverlayWordRangeOperation } from './overlay-placement';
import { sceneDisplayId } from './scene-addressing';

import { setAssetIntroExitTimesOperation } from 'js/src/luminary/asset-timing-operations';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';
import {
    getRootAssetGroup,
    getVoiceDrivenAssetPlaybackRegions,
    getVoiceDrivenBaseAssetSourceDuration,
    hasAppliedMatchCutWithNextScene,
    isVoiceDrivenSceneGroup,
    translateTranscriptTimeToVideoTime,
} from 'js/src/luminary/luminary-selectors';
import {
    getVoicedrivenSceneEndTime,
    getVoicedrivenSceneStartTime,
} from 'js/src/luminary/voicedriven-scenes';

/** The agent-facing timing arguments shared by set_text and set_group_asset. */
export interface AssetTimingArgs {
    start_words?: string[];
    start_time_ms?: number;
}

/** True when either timing argument was supplied, so callers can skip the timing path entirely. */
export function hasAssetTimingArgs(args: AssetTimingArgs): boolean {
    return args.start_words !== undefined || args.start_time_ms !== undefined;
}

/**
 * An asset's resolved appearance window, in the units `setAssetIntroExitTimesOperation` consumes:
 * milliseconds from the start of the asset's own container (the scene, or its first subscene).
 * `exitTime` is the container's duration — the asset stays visible to the end of its scene.
 */
export type ResolvedAssetTiming =
    | { introTime: number; exitTime: number }
    | {
          error: string;
          /** The earliest point the group rules allow, when the request only broke those rules. */
          earliestAllowed?: { introTime: number; exitTime: number; after: string };
      };

/**
 * The rules a group imposes on its children's timing, checked before anything is written:
 * - a child never appears before any group it sits in (the group is the earliest any of its
 *   children can land), and
 * - the editable text and icon children of a group reveal in order, so an item cannot be timed
 *   earlier than an item above it that is already timed later.
 *
 * Returns a corrective message for the agent, or undefined when the timing is allowed. Shared
 * with the standalone recommend-timing tool so both speak the same rule.
 */
export function findGroupedAssetTimingConflict(
    asset: Asset,
    introTime: number,
): string | undefined {
    let parentGroup: AssetGroup | null = asset.getParentByType(VideoData.GROUP_ASSET_TYPE);
    while (parentGroup) {
        if (parentGroup.introAnimationOffset > introTime) {
            return `"${asset.name}" cannot appear before its group "${parentGroup.name}", which appears at ${Math.round(parentGroup.introAnimationOffset)}ms. Re-time the group first (${TOOL_NAMES.setGroupAsset}), or pick a later point.`;
        }
        parentGroup = parentGroup.getParentByType(VideoData.GROUP_ASSET_TYPE);
    }

    const rootGroup = getRootAssetGroup(asset);
    if (!rootGroup) {
        return undefined;
    }
    const siblings = getRevealableGroupChildren(rootGroup);
    const assetIndex = siblings.findIndex((sibling) => sibling.id === asset.id);
    for (let i = 0; i < assetIndex; i++) {
        const earlierSibling = siblings[i];
        const earlierSiblingAppearsAt = getAssetAppearanceOffset(earlierSibling);
        if (earlierSiblingAppearsAt > introTime) {
            return `"${asset.name}" must appear after "${earlierSibling.name}", which comes before it in the group and appears at ${Math.round(earlierSiblingAppearsAt)}ms. Time the earlier items first, or pick a later point.`;
        }
    }
    return undefined;
}

/**
 * The earliest point the group rules in {@link findGroupedAssetTimingConflict} let an asset
 * appear, and the group or earlier sibling that sets it. Undefined when nothing constrains it.
 */
function getEarliestGroupedAssetTime(
    asset: Asset,
): { introTime: number; after: string } | undefined {
    const constraints: { introTime: number; after: string }[] = [];
    let parentGroup: AssetGroup | null = asset.getParentByType(VideoData.GROUP_ASSET_TYPE);
    while (parentGroup) {
        constraints.push({ introTime: parentGroup.introAnimationOffset, after: parentGroup.name });
        parentGroup = parentGroup.getParentByType(VideoData.GROUP_ASSET_TYPE);
    }
    const rootGroup = getRootAssetGroup(asset);
    if (rootGroup) {
        const siblings = getRevealableGroupChildren(rootGroup);
        const assetIndex = siblings.findIndex((sibling) => sibling.id === asset.id);
        for (let i = 0; i < assetIndex; i++) {
            constraints.push({
                introTime: getAssetAppearanceOffset(siblings[i]),
                after: siblings[i].name,
            });
        }
    }
    return constraints.reduce<{ introTime: number; after: string } | undefined>(
        (latest, constraint) =>
            !latest || constraint.introTime > latest.introTime ? constraint : latest,
        undefined,
    );
}

/**
 * The children of a group that reveal in order: its editable text and icon assets, in tree order.
 * Icons are included because an icon-and-label pair is one item — an icon timed before the label
 * above it is the same defect as a label timed before the label above it.
 */
function getRevealableGroupChildren(group: AssetGroup): Asset[] {
    return group
        .getAssets()
        .filter(
            (child) =>
                (VideoData.isTextAsset(child) || VideoData.isIconAsset(child)) && child.isEditable,
        );
}

/**
 * The assets a timing request applies to. Timing a group means the whole group lands at once, so
 * the request fans out to the group and every leaf inside it: Luminary only lets a group's own
 * offset gate its children when the group carries an object animation, which scene-design groups
 * usually don't — writing the children directly is what makes the group actually move.
 */
export function assetsTimedTogether(asset: Asset): Asset[] {
    if (!VideoData.isAssetGroup(asset)) {
        return [asset];
    }
    return [asset, ...asset.getAssets().filter((child) => !VideoData.isAssetGroup(child))];
}

/**
 * A match cut holds everything on screen where it joins two scenes in place for the whole scene
 * (Luminary skips those assets' entrances), so a timing write to one is silently ignored. Name the
 * match cut and the call that frees the asset, so the agent can decide which it wants.
 */
function describeMatchCutHold(luminary: SafeLuminary, scene: Scene, asset: Asset): string {
    const fromScene = hasAppliedMatchCutWithNextScene(scene) ? scene : scene.prevScene;
    const intoScene = fromScene?.nextScene;
    if (!fromScene || !intoScene) {
        return `"${asset.name}" is held on screen for the whole scene by a match cut, so its timing cannot change.`;
    }
    const fromSceneId = sceneDisplayId(luminary, fromScene.id);
    const intoSceneId = sceneDisplayId(luminary, intoScene.id);
    return `"${asset.name}" is held on screen for the whole scene by the match cut between scene ${fromSceneId} and scene ${intoSceneId}, so its timing cannot change. To time it, first remove the match cut with ${TOOL_NAMES.setMatchCut} (scene_id "${fromSceneId}", enabled: false).`;
}

/**
 * Resolve an asset's requested appearance point without touching the video, so a tool can reject
 * a bad request before it applies any of its other edits.
 *
 * Two ways to express it, mirroring the two kinds of scene:
 * - `start_words`: consecutive transcript tokens (the same vocabulary `add_voiceover_scene` takes,
 *   resolved through the same {@link resolveOverlayWordRangeOperation}), so what the agent reads in
 *   the video state transcript is exactly what it can pass back. Voice-driven scenes only.
 * - `start_time_ms`: milliseconds from the start of the scene. Works on any scene, and is the only
 *   option for text-on-media scenes, which have no transcript to point at.
 *
 * Word matching returns transcript time. That is translated to video time through the scene
 * group's playback regions (so a cut before the word doesn't shift it), checked against the scene's
 * logical coverage (the `covers` range the agent sees), and finally made relative to the asset's
 * container — the same origin Luminary applies the offset from.
 */
export const resolveAssetTimingOperation = createLuminaryOperation(
    (
        { luminary, call },
        scene: Scene,
        asset: Asset,
        args: AssetTimingArgs,
    ): ResolvedAssetTiming => {
        const { start_words, start_time_ms } = args;

        if (start_words !== undefined && start_time_ms !== undefined) {
            return { error: 'Provide either start_words or start_time_ms, not both.' };
        }
        if (start_words === undefined && start_time_ms === undefined) {
            return { error: 'Provide either start_words or start_time_ms.' };
        }

        const container = asset.luminaryParent;
        if (!container) {
            return { error: `"${asset.name}" is not on a scene, so it cannot be timed.` };
        }
        if (VideoData.isSubscene(container) && container.index > 0) {
            return {
                error: `"${asset.name}" is on subscene ${container.index}, which already appears when that subscene starts. Only assets on a scene's first subscene can be timed.`,
            };
        }
        if (assetsTimedTogether(asset).some((timedAsset) => timedAsset.isMatchCutFrozen)) {
            return { error: describeMatchCutHold(luminary, scene, asset) };
        }
        const containerDuration = container.duration;

        let introTime: number;
        if (start_time_ms !== undefined) {
            if (!Number.isFinite(start_time_ms)) {
                return { error: 'start_time_ms must be a number of milliseconds.' };
            }
            if (start_time_ms < 0) {
                return { error: 'start_time_ms cannot be negative.' };
            }
            if (start_time_ms >= containerDuration) {
                return {
                    error: `start_time_ms (${Math.round(start_time_ms)}ms) is longer than the scene itself (${Math.round(containerDuration)}ms), so the asset would never appear.`,
                };
            }
            introTime = start_time_ms;
        } else {
            const sceneGroup = scene.sceneGroup;
            if (!sceneGroup || !isVoiceDrivenSceneGroup(sceneGroup)) {
                return {
                    error: 'This scene is not part of a voice-driven scene group, so it has no transcript to time against. Use start_time_ms instead.',
                };
            }

            const toVideoTime = (transcriptTimeMillis: number) =>
                translateTranscriptTimeToVideoTime(
                    transcriptTimeMillis,
                    getVoiceDrivenAssetPlaybackRegions(sceneGroup),
                    getVoiceDrivenBaseAssetSourceDuration(sceneGroup),
                    sceneGroup.startTime,
                );
            const sceneStartTime = getVoicedrivenSceneStartTime(scene);
            const sceneEndTime = getVoicedrivenSceneEndTime(scene);
            const isInScene = (videoTime: number) =>
                videoTime >= sceneStartTime && videoTime < sceneEndTime;

            // The same words often recur across the transcript ("The", a repeated name); take
            // the occurrence this scene covers rather than the first one anywhere.
            const range = call(
                resolveOverlayWordRangeOperation,
                sceneGroup,
                start_words!,
                ({ startTimeMillis }) => isInScene(toVideoTime(startTimeMillis)),
            );
            if ('error' in range) {
                return range;
            }

            const wordVideoTime = toVideoTime(range.startTimeMillis);
            if (!isInScene(wordVideoTime)) {
                return {
                    error: `The words "${start_words!.join(' ')}" are spoken outside this scene's time range, so the asset would never appear. Pick words this scene covers (see its \`covers\` field in the video state), or use start_time_ms.`,
                };
            }
            introTime = Math.min(
                Math.max(0, wordVideoTime - container.startTime),
                containerDuration,
            );
        }

        const conflict = findGroupedAssetTimingConflict(asset, introTime);
        if (conflict) {
            const earliest = getEarliestGroupedAssetTime(asset);
            return {
                error: conflict,
                ...(earliest && earliest.introTime < containerDuration
                    ? { earliestAllowed: { ...earliest, exitTime: containerDuration } }
                    : {}),
            };
        }

        return { introTime, exitTime: containerDuration };
    },
);

/**
 * What a tool does with a resolved timing request, given whether the call carries other edits.
 * Timing alone keeps its hard error. Alongside other edits a timing problem must not throw those
 * edits away (the agent would redo them all in another turn): a request that only broke the group
 * reveal order is moved to the earliest allowed point, and anything else is skipped with a note.
 */
export type AssetTimingPlan =
    | { timing: { introTime: number; exitTime: number }; args: AssetTimingArgs; note?: string }
    | { skipNote: string }
    | { error: string };

export function planAssetTiming(
    resolved: ResolvedAssetTiming,
    args: AssetTimingArgs,
    hasOtherEdits: boolean,
): AssetTimingPlan {
    if (!('error' in resolved)) {
        return { timing: resolved, args };
    }
    if (!hasOtherEdits) {
        return { error: resolved.error };
    }
    if (resolved.earliestAllowed) {
        const { after, ...timing } = resolved.earliestAllowed;
        return {
            timing,
            args: { start_time_ms: timing.introTime },
            note: `timed after ${after}, which comes before it in the group`,
        };
    }
    return { skipNote: `timing not applied: ${resolved.error}` };
}

/** The outcome of applying resolved timing, phrased for the tool result. */
export type AppliedAssetTiming = { change: string } | { warning: string };

/** Appends a plan's adjustment note to the applied change, so the agent sees why it moved. */
export function withTimingNote(applied: AppliedAssetTiming, note?: string): AppliedAssetTiming {
    return note && 'change' in applied ? { change: `${applied.change} (${note})` } : applied;
}

/**
 * Write a resolved appearance point, leaving the asset visible to the end of its container (the
 * same exit convention the standalone timing agent uses). Shared by set_text and set_group_asset
 * so both write through one path. Call it after the tool's other edits, then checkpoint, so the
 * checkpoint captures the asset's final content along with its offsets.
 *
 * The container is re-read here because an edit in between (new text re-fitting a text-on-media
 * scene's duration) can shrink it past the requested point; that comes back as a warning for the
 * tool to report alongside its other changes rather than as a failed call.
 */
export const applyResolvedAssetTimingOperation = createLuminaryOperation(
    (
        { call },
        scene: Scene,
        asset: Asset,
        resolved: { introTime: number; exitTime: number },
        args: AssetTimingArgs,
        parentActionGroup: string,
    ): AppliedAssetTiming => {
        const containerDuration = asset.luminaryParent?.duration ?? resolved.exitTime;
        if (resolved.introTime >= containerDuration) {
            return {
                warning: `timing not applied: ${Math.round(resolved.introTime)}ms is past the scene's new duration (${Math.round(containerDuration)}ms)`,
            };
        }

        call(setAssetIntroExitTimesOperation, {
            timingSpecifications: assetsTimedTogether(asset).map((timedAsset) => ({
                sceneId: scene.id,
                assetId: timedAsset.id,
                introTime: resolved.introTime,
                exitTime: containerDuration,
            })),
            operatingOnMainLuminary: true,
            systemTriggered: false,
            parentActionGroup,
        });

        const when =
            args.start_words !== undefined
                ? `"${args.start_words.join(' ')}"`
                : `${Math.round(resolved.introTime)}ms into the scene`;
        return { change: `appears at ${when}` };
    },
);
